import { useContext, useState, useCallback, useEffect, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { DynamicCheckbox } from "../Inputs/Inputs";
import "./HideChat.scss";
import AlertModal from "../AlertModal/AlertModal";

export const HideChat = ({ isOpen }) => {
  const { axiosAuth, accessibleGroups } = useContext(AuthContext);

  // true = HIDDEN (checkbox marcada), false = SHOWN
  const [hiddenByGroup, setHiddenByGroup] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  const groupIds = useMemo(
    () => accessibleGroups.map((g) => g.id),
    [accessibleGroups]
  );

  const fetchVisibilities = useCallback(async () => {
    if (!isOpen || groupIds.length === 0) return;
    setLoading(true);
    setErrorModal("");
    try {
      // N chamadas em paralelo (uma por grupo)
      const calls = groupIds.map((gid) =>
        axiosAuth
          .get(`/chat-settings/group/${gid}/visibility`)
          .then(({ data }) => ({ gid, is_visible: Boolean(data?.is_visible) }))
          .catch(() => ({ gid, is_visible: true }))
      );

      const results = await Promise.allSettled(calls);

      const next = {};
      for (const r of results) {
        if (r.status === "fulfilled") {
          const { gid, is_visible } = r.value;
          next[gid] = !is_visible;
        } else {
          // fallback seguro
          const { gid } = r.reason ?? {};
          if (gid) next[gid] = false;
        }
      }

      // garante chave para todos os grupos
      for (const gid of groupIds) if (!(gid in next)) next[gid] = false;
      setHiddenByGroup(next);
    } catch (err) {
      openError(
        "Erro ao carregar opções de visibilidade",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, isOpen, groupIds, openError]);

  useEffect(() => {
    fetchVisibilities();
  }, [fetchVisibilities]);

  const handleChange = useCallback(
    async (gid, checked) => {
      if (!gid) return;
      setHiddenByGroup((prev) => ({ ...prev, [gid]: checked }));
      setSavingId(gid);
      setErrorModal("");
      try {
        await axiosAuth.put(`/chat-settings/group/${gid}/visibility`, {
          is_visible: !checked,
        });
      } catch (err) {
        setHiddenByGroup((prev) => ({ ...prev, [gid]: !checked }));
        openError(
          "Erro ao alterar opções de visibilidade",
          err?.response?.data?.detail || err?.message
        );
      } finally {
        setSavingId(null);
      }
    },
    [axiosAuth, openError]
  );

  return (
    <div className={`hide-chat ${isOpen ? "opened" : ""}`}>
      {errorModal.open && (
        <AlertModal
          onClose={closeError}
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
        />
      )}
      <div className="container">
        <div className="top-section">
          <h3>Esconder chat</h3>
        </div>
        <div className="bottom-section">
          {accessibleGroups &&
            accessibleGroups.map((g) => {
              const checked = Boolean(hiddenByGroup[g.id]);
              const disabled = savingId === g.id || loading;
              return (
                <div className="each" key={g.id}>
                  <DynamicCheckbox
                    onChange={(_name, checked) => handleChange(g.id, checked)}
                    label={g.name}
                    name={`hide-${g.id}`}
                    checked={checked}
                    disabled={disabled}
                  />
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
